import { supabase } from "@/integrations/supabase/client";

/**
 * Campaign (UTM) tagging + social-preview A/B variants for shared blueprint URLs.
 * Every public view records the campaign it arrived on and the preview variant it
 * saw, so the dashboard can attribute purchases to a channel and a variant.
 */

export type Campaign = {
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  variant_key?: string | null;
};

export const CAMPAIGN_PRESETS: { label: string; value: Campaign }[] = [
  { label: "X / Twitter post", value: { utm_source: "twitter", utm_medium: "social" } },
  { label: "LinkedIn post", value: { utm_source: "linkedin", utm_medium: "social" } },
  { label: "Reddit comment", value: { utm_source: "reddit", utm_medium: "social" } },
  { label: "Newsletter", value: { utm_source: "newsletter", utm_medium: "email" } },
  { label: "Discord / Slack DM", value: { utm_source: "chat", utm_medium: "referral" } },
];

/** Builds a shareable URL for a slug with optional campaign + variant tags. */
export function campaignShareUrl(slug: string, c: Campaign = {}): string {
  const url = new URL(`/b/${slug}`, window.location.origin);
  (["utm_source", "utm_medium", "utm_campaign", "utm_content"] as const).forEach((k) => {
    const v = c[k]?.trim();
    if (v) url.searchParams.set(k, v.slice(0, 60));
  });
  if (c.variant_key?.trim()) url.searchParams.set("v", c.variant_key.trim().slice(0, 24));
  return url.toString();
}

/** Reads campaign + variant tags off the current URL (safe, capped values). */
export function readCampaignFromUrl(search: string): Campaign {
  const q = new URLSearchParams(search);
  const clean = (v: string | null) =>
    v ? v.trim().slice(0, 60).replace(/[^\w.\-/ ]/g, "") || null : null;
  return {
    utm_source: clean(q.get("utm_source")),
    utm_medium: clean(q.get("utm_medium")),
    utm_campaign: clean(q.get("utm_campaign")),
    utm_content: clean(q.get("utm_content")),
    variant_key: clean(q.get("v")),
  };
}

export type ChannelAnalyticsRow = {
  slug: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  views: number;
  unique_visitors: number;
  signups: number;
  purchases: number;
  revenue_cents: number;
  last_event_at: string | null;
};

export async function fetchChannelAnalytics(): Promise<ChannelAnalyticsRow[]> {
  const { data, error } = await supabase.rpc("share_channel_analytics_for_user");
  if (error) throw new Error(error.message);
  return (data as ChannelAnalyticsRow[]) ?? [];
}

export type VariantPerformanceRow = {
  share_id: string;
  slug: string;
  variant_key: string;
  og_title: string | null;
  is_active: boolean;
  views: number;
  unique_visitors: number;
  signups: number;
  purchases: number;
  revenue_cents: number;
};

export async function fetchVariantPerformance(): Promise<VariantPerformanceRow[]> {
  const { data, error } = await supabase.rpc("share_variant_performance_for_user");
  if (error) throw new Error(error.message);
  return (data as VariantPerformanceRow[]) ?? [];
}

export type ShareVariant = {
  id: string;
  share_id: string;
  variant_key: string;
  og_title: string | null;
  og_description: string | null;
  og_image_url: string | null;
  weight: number;
  is_active: boolean;
};

export async function loadShareVariants(slug: string): Promise<ShareVariant[]> {
  const { data: share } = await supabase
    .from("blueprint_shares")
    .select("id")
    .eq("slug", slug)
    .maybeSingle();
  if (!share?.id) return [];
  const { data, error } = await supabase
    .from("blueprint_share_variants")
    .select("id,share_id,variant_key,og_title,og_description,og_image_url,weight,is_active")
    .eq("share_id", share.id)
    .order("variant_key");
  if (error) throw new Error(error.message);
  return (data as ShareVariant[]) ?? [];
}

export async function upsertShareVariant(
  slug: string,
  variant: Omit<ShareVariant, "id" | "share_id"> & { id?: string },
): Promise<void> {
  const { data: share } = await supabase
    .from("blueprint_shares")
    .select("id")
    .eq("slug", slug)
    .maybeSingle();
  if (!share?.id) throw new Error("Share link not found");
  const key = variant.variant_key.trim().toUpperCase().slice(0, 24);
  if (!/^[A-Z0-9_-]{1,24}$/.test(key)) throw new Error("Variant key must be letters/numbers");
  if (variant.og_image_url && !/^https:\/\/\S+$/.test(variant.og_image_url)) {
    throw new Error("The preview image must be a full https:// URL");
  }
  const { error } = await supabase.from("blueprint_share_variants").upsert(
    {
      share_id: share.id,
      variant_key: key,
      og_title: variant.og_title?.trim().slice(0, 90) || null,
      og_description: variant.og_description?.trim().slice(0, 200) || null,
      og_image_url: variant.og_image_url?.trim() || null,
      weight: Math.max(0, Math.min(100, Math.round(variant.weight))),
      is_active: variant.is_active,
    },
    { onConflict: "share_id,variant_key" },
  );
  if (error) throw new Error(error.message);
}

export async function deleteShareVariant(id: string): Promise<void> {
  const { error } = await supabase.from("blueprint_share_variants").delete().eq("id", id);
  if (error) throw new Error(error.message);
}
